import { MouseEvent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  APPLE_AUTH_ENABLED,
  FORGOT_PASSWORD_CLIENT_DOMAIN,
  GOOGLE_AUTH_ENABLED,
  MSAL_ENABLED,
  PUBLIC_KEY_PEM,
  REDIRECT_URL,
  TAX1099_REDIRECT_URL,
} from '../../constants/global.constants';
import { AUTH_UI_TEXT } from '../../constants/auth.constants';
import { zenworksServiceApi } from '../../service/zenworksService.api';
import { AppDispatch, RootState } from '../../redux/store';
import ButtonWithLoading from '../common/ButtomWIthLoading';
import ShowPasswordInput from '../common/ShowPasswordInput';
import SignUp from './SignUp';
import MsalAuth from './MsalAuth';
import Otp from './Otp';
import Tfa from './Tfa';
import socket from '../../socket';
import DisplayInfoMessage from '../common/DisplayInfoMessage';
import { isValidEmail, isValidPassword } from '../../utils/validations';
import { encryptMessageWithRSAOAEP } from '../../utils/encryption';
import { generateId, saveTMIToken, getTMIToken } from '../../utils/functions';
import { postToParent } from '../../utils/postMessage';

type AuthProps = {
  handleCardClick: (card: any) => void;
  cardScreen: string;
  title?: string;
};

function Auth(props: AuthProps) {
  const { cardScreen, title } = props;

  const socketC = useSelector((state: RootState) => state.socketConnection);
  const { status: connectionStatus } = socketC;

  const [isLoading, setIsLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [screen, setScreen] = useState<string>(() => {
  return localStorage.getItem('authScreen') || cardScreen || 'Login';
});
  const [displayMessage, setDisplayMessage] = useState({
    error: false,
    message: '',
  });
  const dispatch = useDispatch<AppDispatch>();
  const [, setResponsePayload] = useState<any>(null);
  const [form, setForm] = useState({
    email: '',
    password: '',
  });
  const [signupData, setSignupData] = useState<any>(null);

  // OTP verification state
  const [otpForm, setOtpForm] = useState({
    isLoading: false,
    connectionId: '',
    otp: '',
    stateId: '',
    userId: '',
    isSignup: false,
  });

  // TFA verification state
  const [tfaForm, setTfaForm] = useState({
    connectionId: '',
    verificationCode: '',
    userId: '',
    isLoading: false,
  });

  // Get client type for dynamic styling
  const clientType = process.env.REACT_APP_CLIENT;
  const primaryColor = clientType === 'salesbot2290ai' ? '#16A34A' : '#114175';
  const hoverColor = clientType === 'salesbot2290ai' ? '#15803D' : '#6491C3';
  const textColor = clientType === 'salesbot2290ai' ? 'text-[#16A34A]' : 'text-[#114175]';
  const focusRingColor =
    clientType === 'salesbot2290ai' ? 'focus:ring-[#16A34A]' : 'focus:ring-[#114175]';

  const handleSendToken = (connectionId: string, callbackUrl: string = "") => {
    postToParent('SEND_IDENTIFIER', { identifier: connectionId, callbackUrl });
  };

  const handelDisplayMessage = ({ error = true, message }: any) => {
    setDisplayMessage({ error, message });
    setTimeout(() => {
      setDisplayMessage({
        error: false,
        message: '',
      });
    }, 5000);
  };

  useEffect(() => {
    if(screen){
      localStorage.setItem('authScreen', screen);
    }
  }, [screen]);

  // Check if user already has a token on mount
  useEffect(() => {
    const token = getTMIToken();
    const userEmail = localStorage.getItem('userEmail');
    const userName = localStorage.getItem('name');
    
    // If token exists and we're on SignUp screen, show success screen
    if (token && cardScreen === 'SignUp') {
      console.log('✅ Token found, showing success screen');
      setScreen('signup-success');
      
      // Pre-fill email if available
      if (userEmail) {
        setForm(prev => ({ ...prev, email: userEmail }));
      }
      
      handelDisplayMessage({
        error: false,
        message: userName 
          ? `Welcome back, ${userName.split(' ')[0]}! You're all set.`
          : 'Your account was created successfully!',
      });
    }
  }, [cardScreen]);

  // Updated handleLogin function with cross-origin support
  const handleLogin = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // Encrypt the password
      const encryptedPassword = await encryptMessageWithRSAOAEP(form.password, PUBLIC_KEY_PEM);

      // Try iframe authentication first
      try {
        console.log('🔄 Attempting cross-origin iframe authentication...');
        const connectionId = generateId();
        setForm((prev) => ({ ...prev, connectionId }));
        const response = await zenworksServiceApi.idpLogin({
          email: form.email,
          password: encryptedPassword,
          connectionId,
        });
        if (response.success) {
          if (response?.content?.source === 'AccessToken' && (response?.content?.access_token || response?.content?.accessToken || response?.content?.accesstoken)) {
            handleAuthSuccess(response.content, connectionId);
          } else if (response?.content?.source === 'TwoFactorEnabled') {
            console.log('🔐 2FA verification required');
            handleTfaRequired({ ...response.content, connectionId });
          } else if (response.content?.source === 'OtpRequired') {
            handleOtpRequired({ ...response.content, connectionId });
          } else if (response?.content?.source === 'UserNotFound') {
            handleAuthError({ message: AUTH_UI_TEXT.ERROR_USER_NOT_FOUND });
          } else if (response?.content?.source === 'IncorrectPwd') {
            handleAuthError({ message: AUTH_UI_TEXT.ERROR_INCORRECT_PASSWORD });
          } else if (
            response?.content?.source === 'PwdAttemptExceed' ||
            response?.content?.source === 'locked'
          ) {
            handleAuthError({
              message: AUTH_UI_TEXT.ERROR_ACCOUNT_LOCKED,
            });
          }
        } else {
          if (response?.content?.source === 'TwoFactorEnabled') {
            console.log('🔐 2FA verification required');
            handleTfaRequired(response.content);
          } else if (response.content?.source === 'OtpRequired') {
            handleOtpRequired(response.content);
          } else if (
            response?.content?.source === 'UserNotFound' ||
            response?.content?.source === 'IncorrectPwd' ||
            response?.content?.source === 'PwdAttemptExceed' ||
            response?.content?.source === 'locked'
          ) {
            handleAuthError({ message: AUTH_UI_TEXT.ERROR_INVALID_CREDENTIALS });
          } else {
            handleAuthError(response || AUTH_UI_TEXT.ERROR_SOMETHING_WRONG);
          }
        }
      } catch (error: any) {
        console.log('⚠️ Iframe authentication failed:', error.message);
      }
    } catch (error: any) {
      console.error('❌ Login error:', error);
      handelDisplayMessage({
        error: true,
        message: error.message || AUTH_UI_TEXT.ERROR_AUTH_FAILED,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSuccess = (payload: any, connectionId: string) => {
    try {
      // Handle successful authentication
      let customerUsername = `${payload.FirstName || ''} ${payload.MiddleName || ''} ${payload.LastName || ''}`;
      customerUsername = customerUsername.replace(/\s+/g, ' ').trim();

      if (payload.access_token) {
        saveTMIToken(payload.access_token);
      } else if (payload.accessToken) {
        saveTMIToken(payload.accessToken);
      } else if (payload.accesstoken) {
        saveTMIToken(payload.accesstoken);
      }

      localStorage.setItem('name', customerUsername);
      setScreen('logged-in');

      dispatch({ type: 'SET_USER', payload: payload });
      dispatch({
        type: 'SET_AUTH',
        payload: {
          auth: true,
          type: 'showReferenceNumberCard',
        },
      });
      socket.emit('user_login_in', { email: form.email });
      // handleCardClick('existingTax1099User');

      handelDisplayMessage({
        error: false,
        message: AUTH_UI_TEXT.LOGIN_SUCCESS,
      });
      if (payload.access_token || payload.accessToken || payload.accesstoken ) {
        handleSendToken(connectionId);
      }
    } catch (error: any) {
      handelDisplayMessage({
        error: true,
        message: AUTH_UI_TEXT.ERROR_AUTH_RESPONSE,
      });
    }
  };

  const handleAuthError = (payload: any) => {
    handelDisplayMessage({
      error: true,
      message: payload?.message || AUTH_UI_TEXT.ERROR_AUTH_FAILED,
    });
  };

  // TFA Required Handler
  const handleTfaRequired = (payload: any) => {
    setTfaForm({
      connectionId: payload.connectionId,
      verificationCode: '',
      userId: payload.userId,
      isLoading: false,
    });

    setScreen('VerifyTfa');

    handelDisplayMessage({
      error: false,
      message: AUTH_UI_TEXT.SUCCESS_TFA_PROMPT,
    });
  };

  const handleOtpRequired = (payload: any, isSignup: boolean = false) => {
    setOtpForm({
      connectionId: payload.connectionId,
      otp: '',
      stateId: payload.stateId,
      userId: payload.userId,
      isLoading: false,
      isSignup: isSignup,
    });
    setScreen('VerifyOtp');

    handelDisplayMessage({
      error: false,
      message: AUTH_UI_TEXT.SUCCESS_OTP_SENT,
    });
  };

  // OTP Verification API Call (IDP)
  const handleVerifyOtp = async (otpCode: string) => {
    try {
      setOtpForm((prev) => ({ ...prev, isLoading: true }));

      const result: any = await zenworksServiceApi.idpVerifyOtp({
        otp: otpCode.toString(),
        connectionId: otpForm.connectionId,
        userId: otpForm.userId.toString(),
        stateId: otpForm.stateId,
      });
      if (result.success && result?.content?.accessToken) {
        saveTMIToken(result.content.accessToken);
        setResponsePayload(result.content);

        const callbackUrl = "https://web.tax1099.com/#/";

        if (otpForm.isSignup) {
          handleSignupSuccess(result.content, otpForm.connectionId, callbackUrl);
        } else {
          // OTP verified successfully, proceed with login
          handleAuthSuccess(result.content, otpForm.connectionId);
        }
      } else {
        handelDisplayMessage({
          error: true,
          message: result.message || AUTH_UI_TEXT.ERROR_INVALID_OTP,
        });
      }
    } catch (error: any) {
      handelDisplayMessage({
        error: true,
        message: error.message || AUTH_UI_TEXT.ERROR_OTP_VERIFICATION_FAILED,
      });
    } finally {
      setOtpForm((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // Resend OTP (IDP)
  const handleResendOtp = async () => {
    try {
      setIsResendLoading(true);
      const result: any = await zenworksServiceApi.idpResendOtp({
        stateId: otpForm.stateId,
      });

      if (result.success) {
        setResponsePayload(result.content);

        handelDisplayMessage({
          error: false,
          message: AUTH_UI_TEXT.OTP_SUCCESS,
        });
      } else {
        handelDisplayMessage({
          error: true,
          message: result.message || AUTH_UI_TEXT.ERROR_OTP_RESEND_FAILED,
        });
      }
    } catch (error: any) {
      handelDisplayMessage({
        error: true,
        message: error.message || AUTH_UI_TEXT.ERROR_OTP_RESEND_FAILED,
      });
    } finally {
      setIsResendLoading(false);
    }
  };

  // TFA Verification API Call (IDP)
  const handleVerifyTfa = async (tfaCode: string) => {
    try {
      setTfaForm((prev) => ({ ...prev, isLoading: true }));
      let connectionId = tfaForm.connectionId;
      if (!connectionId) {
        connectionId = generateId();
      }
      const result: any = await zenworksServiceApi.idpTFALogin({
        userId: tfaForm.userId,
        verificationCode: tfaCode,
        connectionId: tfaForm?.connectionId ?? connectionId,
      });
      if (result.success && result?.content?.accesstoken) {
        setResponsePayload(result.content);

        saveTMIToken(result.content.accesstoken);
        // TFA verified successfully, proceed with login
        handleAuthSuccess(result.content, tfaForm?.connectionId ?? connectionId);
      } else {
        handelDisplayMessage({
          error: true,
          message: result.message || AUTH_UI_TEXT.ERROR_INVALID_TFA,
        });
      }
    } catch (error: any) {
      handelDisplayMessage({
        error: true,
        message: error.message || AUTH_UI_TEXT.ERROR_TFA_VERIFICATION_FAILED,
      });
    } finally {
      setTfaForm((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handelScreen = (screenName: string) => {
    setScreen(screenName);

    // Clear forms when switching screens
    if (screenName === 'Login') {
      setDisplayMessage({
        error: false,
        message: '',
      });
      setSignupData(null);
    }
  };

  const isPasswordValid = isValidPassword(form.password);
  const isEmailValid = isValidEmail(form.email);

  const handleSignupSuccess = (result: any, connectionId: string, callbackUrl: string = "") => {
    console.log('🔄 SignUp result:', result);
    console.log('💾 Using stored signup data:', signupData);

    // Merge the OTP verification result with stored signup data
    const userData = {
      ...result.content,
      ...signupData,
    };

    // Store user email for login
    localStorage.setItem('userEmail', userData.email || form.email);
    localStorage.setItem('name', userData.FirstName || userData.fullName || form.email);
    
    // Clear signup data
    setSignupData(null);
    
    // Show success screen (not logged in yet)
    setScreen('signup-success');
    
    // Send connectionId to parent
    handleSendToken(connectionId, callbackUrl);

    handelDisplayMessage({
      error: false,
      message: AUTH_UI_TEXT.SIGNUP_SUCCESS_TITLE,
    });
  };

  const handelSignUp = (result: any) => {
    console.log('🔄 SignUp result:', result);
    if (result.success) {
      setForm((prev) => ({ ...prev, email: result.email }));
      setResponsePayload(result);

      const callbackUrl = "https://web.tax1099.com/#/";
      
      // Check if OTP is required
      if (result.content && result.message === 'OtpRequired') {
        setSignupData(result.content);
        handleOtpRequired(result.content, true);
      } else {
        // Direct signup success without OTP
        handleSignupSuccess(result, result?.content?.connectionId, callbackUrl);
      }
    } else {
      // Handle error case
      if (result.content && result.message === 'OtpRequired') {
        setSignupData(result.content);
        handleOtpRequired(result.content, true);
      } else {
        handelDisplayMessage({
          error: true,
          message: result.message,
        });
      }
    }
  };

  const handleForgotPassword = () => {
    window.open(`${FORGOT_PASSWORD_CLIENT_DOMAIN}`, '_blank');
  };

  const handleRedirectToDashboard = () => {
    const email = localStorage.getItem('userEmail') || '';
    
    if (typeof window !== 'undefined') {
      let targetUrl = '';
      
      if (clientType === 'salesbot2290ai') {
        targetUrl = `${REDIRECT_URL}/login?email=${encodeURIComponent(email)}`;
      } else {
        // For tax1099 or other clients, redirect to the main website
        targetUrl = TAX1099_REDIRECT_URL || 'https://hybridwebstage.1099cloud.com/';
      }

      // Clear signup-specific data (keep token for main website)
      localStorage.removeItem('userEmail');
      
      try {
        // Try to redirect the parent window (if in iframe)
        if (window.top && window.top !== window.self) {
          window.top.location.href = targetUrl;
        } else {
          // If not in iframe or can't access parent, open in same window
          window.location.href = targetUrl;
        }
      } catch (err) {
        // Fallback: open in new tab if cross-origin issues
        window.open(targetUrl, '_blank');
      }
    }
  };

  const isFormValid = form.email.trim() !== '' && form.password.trim() !== '' && isEmailValid;

  return (
    <div>
      {screen === 'SignUp' && <SignUp handelScreen={handelScreen} handelSignUp={handelSignUp} title={title} />}
      {screen === 'VerifyOtp' && (
        <Otp
          email={form.email}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          onBackToLogin={() => handelScreen('SignUp')}
          isLoading={otpForm.isLoading}
          isResendLoading={isResendLoading}
          displayMessage={displayMessage}
        />
      )}
      {screen === 'VerifyTfa' && (
        <Tfa
          email={form.email}
          onVerify={handleVerifyTfa}
          onBackToLogin={() => handelScreen('SignUp')}
          isLoading={tfaForm.isLoading}
          displayMessage={displayMessage}
        />
      )}
      {screen === 'Login' && (
        <div className="p-4 sm:p-6 max-w-lg mx-auto w-full">
          {/* Title from AI */}
          {title && (
            <div className="text-xs text-left mb-3 sm:mb-4 text-gray-600 leading-relaxed">
              {title}
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">{AUTH_UI_TEXT.LOGIN_TITLE}</h3>
            <p className="text-sm text-gray-600 mt-2">
              {AUTH_UI_TEXT.LOGIN_NEW_USER}{' '}
              <button
                className={`${textColor} hover:underline font-semibold`}
                onClick={() => handelScreen('SignUp')}
              >
                {AUTH_UI_TEXT.LOGIN_CREATE_ACCOUNT}
              </button>
            </p>
          </div>

          {/* Email Field */}
          <div className="mb-3 sm:mb-4">
            <label htmlFor="Email" className="block text-sm font-medium text-gray-700 mb-1">
              {AUTH_UI_TEXT.LOGIN_EMAIL_LABEL} <span className="text-red-500">*</span>
            </label>
            <input
              name="Email"
              type="email"
              id="Email"
              className={`block py-2.5 px-3 w-full text-sm text-gray-900 bg-white border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 ${focusRingColor} focus:border-transparent`}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={connectionStatus !== 'Connected'}
            />
            {form.email && !isEmailValid && (
              <span className="text-xs text-red-600 mt-1 block">{AUTH_UI_TEXT.VALIDATION_EMAIL_INVALID}</span>
            )}
          </div>

          {/* Password Field */}
          <div className="mb-3 sm:mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              {AUTH_UI_TEXT.LOGIN_PASSWORD_LABEL} <span className="text-red-500">*</span>
            </label>
            <ShowPasswordInput
              name="password"
              id="password"
              label={''}
              className="relative"
              onChange={(e: any) => setForm({ ...form, password: e.target.value })}
              value={form.password}
              isPasswordValid={isPasswordValid}
              showPasswordRules={false}
              focusRingColor={focusRingColor}
            />
          </div>

          {/* Error/Success Message */}
          <DisplayInfoMessage displayMessage={displayMessage} />

          {/* Forgot Password Link */}
          <div className="text-left mb-4">
            <button
              type="button"
              className={`text-sm ${textColor} hover:underline font-semibold`}
              onClick={() => handleForgotPassword()}
            >
              {AUTH_UI_TEXT.LOGIN_FORGOT_PASSWORD}
            </button>
          </div>

          {/* Login Button */}
          <ButtonWithLoading
            isLoading={isLoading}
            label={AUTH_UI_TEXT.LOGIN_BUTTON}
            type="button"
            className={`w-full py-3 rounded-full text-white font-semibold text-base transition-colors ${
              isFormValid && connectionStatus === 'Connected'
                ? 'cursor-pointer'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            style={
              isFormValid && connectionStatus === 'Connected'
                ? { backgroundColor: primaryColor }
                : {}
            }
            onMouseEnter={(e: any) => {
              if (isFormValid && connectionStatus === 'Connected') {
                e.currentTarget.style.backgroundColor = hoverColor;
              }
            }}
            onMouseLeave={(e: any) => {
              if (isFormValid && connectionStatus === 'Connected') {
                e.currentTarget.style.backgroundColor = primaryColor;
              }
            }}
            disabled={!isFormValid || connectionStatus !== 'Connected'}
            onClick={handleLogin}
          />

          {/* Social Login Separator */}
          {(GOOGLE_AUTH_ENABLED || APPLE_AUTH_ENABLED || MSAL_ENABLED) && (
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">{AUTH_UI_TEXT.LOGIN_OR_TEXT}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Social Auth */}
      <div className="flex flex-row justify-center">{MSAL_ENABLED && <MsalAuth />}</div>

      {/* Logged In State */}
      {screen === 'logged-in' && (
        <div className="p-4 sm:p-6 text-center">
          <div className="text-lg font-semibold text-gray-900 mb-2">{AUTH_UI_TEXT.LOGIN_LOGGED_IN_TITLE}</div>
          <p className="text-sm text-gray-600">{AUTH_UI_TEXT.LOGIN_LOGGED_IN_MESSAGE}</p>
        </div>
      )}

      {/* Signup Success State */}
      {screen === 'signup-success' && (
        <div className="p-4 sm:p-6 max-w-lg mx-auto w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              {AUTH_UI_TEXT.SIGNUP_SUCCESS_TITLE}
            </h3>
            <p className="text-base font-semibold text-gray-800 mb-3">
              {AUTH_UI_TEXT.SIGNUP_SUCCESS_WELCOME}
            </p>
            <p className="text-sm text-gray-600 mb-6">
              {AUTH_UI_TEXT.SIGNUP_SUCCESS_MESSAGE}
            </p>
          </div>

          <DisplayInfoMessage displayMessage={displayMessage} />

          <ButtonWithLoading
            isLoading={false}
            label={AUTH_UI_TEXT.SIGNUP_SUCCESS_BUTTON}
            type="button"
            className="w-full py-3 rounded-full text-white font-semibold text-base transition-colors cursor-pointer"
            style={{ backgroundColor: primaryColor }}
            onMouseEnter={(e: any) => {
              e.currentTarget.style.backgroundColor = hoverColor;
            }}
            onMouseLeave={(e: any) => {
              e.currentTarget.style.backgroundColor = primaryColor;
            }}
            onClick={handleRedirectToDashboard}
          />
        </div>
      )}
    </div>
  );
}

export default Auth;
